<template>
  <component-wrapper>
    <grid-container-fi>
      <div class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12">
        <div
          :class="{
            'image-fi': true,
            'image-fi--with-caption': caption,
          }"
        >
          <template v-if="isEditMode">
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="image-fi__editable-image"
              v-html="fields.image?.editable"
            />
            <div class="image-fi__caption">
              <span class="fi-mr-6">Caption:</span><span v-html="fields.imageCaption?.editable" />
            </div>
            <!-- eslint-enable vue/no-v-html -->
          </template>
          <template v-else>
            <component
              :is="href ? 'a' : 'span'"
              :href="href"
              :class="href ? 'image-fi__link' : null"
            >
              <ssr-cloud-image-fi
                :field="fields.image"
                :breakpoints="breakpoints"
                :unlimited="unlimited"
                class="image-fi__img"
              />
            </component>
          </template>

          <div
            v-if="caption"
            class="image-fi__caption"
          >
            {{ caption }}
          </div>
        </div>
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { SsrCloudImageFi } from 'atoms';
import { computed } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { type ScreenWidth, useBreakpoints } from '@/composables/breakpoint';
import { type MarkSsrOnly, useSsrOnly } from '@/composables/ssrOnly';
import type { ImageField, LinkField, SingleLineTextField } from '@/sitecoreFieldTypes';
import containerPaddings from '@/tailwind/containerPaddings';

defineOptions({
  name: 'ImageFi',
});

const onCreatedMarkAsSsrOnly = (originalOnCreatedMarkAsSsrOnly: () => void) => {
  if (IS_SSR) {
    originalOnCreatedMarkAsSsrOnly();
  }
};

const props = withDefaults(
  defineProps<{
    fields?: {
      image?: ImageField;
      imageCaption?: SingleLineTextField;
      imageLink?: LinkField;
    };
    markSsrOnly?: MarkSsrOnly;
  }>(),
  {
    fields: () => ({}),
    markSsrOnly: undefined,
  },
);

useSsrOnly(props, onCreatedMarkAsSsrOnly);
const { screenWidths } = useBreakpoints();

const href = computed(() => props.fields.imageLink?.value?.href);

const caption = computed(() => props.fields?.imageCaption?.value);

const unlimited = screenWidths.lg - 2 * containerPaddings.lg;

const createBreakpoints = () => {
  try {
    const breakpoints = Object.keys(screenWidths).map((key: string) => {
      const keyTyped = key as keyof ScreenWidth;
      return {
        mediaWidth: screenWidths[keyTyped],
        imageWidth: screenWidths[keyTyped] - 2 * containerPaddings[keyTyped],
      };
    });
    breakpoints.sort((a, b) => (a.mediaWidth > b.mediaWidth ? 1 : 0));
    return breakpoints;
  } catch (error) {
    console.error('Could not create breakpoints', error);
    return [];
  }
};

const breakpoints = createBreakpoints();
</script>

<style scoped src="./image-fi" />
